import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    makeStyles,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/Delete'
import AddIcon from '@material-ui/icons/Add'
import {
    useApplicationList,
    useCreateApplication,
    useApplicationDetail,
    useUpdateApplication,
    useDeleteApplication,
    ApplicationCreateUpdatePayload,
} from './hooks/useAdminApplicationAccess'
import { Application } from '../../../models/Company'
import { GLOBAL_TOP_BAR_HEIGHT } from '../../../constants'

function formatPrice(price: string): string {
    const n = parseFloat(price)
    if (Number.isNaN(n)) return price
    return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
    }).format(n)
}

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        height: `calc(100vh - ${GLOBAL_TOP_BAR_HEIGHT}px)`,
        padding: theme.spacing(2),
    },
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    tableContainer: {
        flex: 1,
        minHeight: 0,
    },
    formField: {
        marginBottom: theme.spacing(2),
    },
}))

const formFieldStyle = { marginBottom: 16 }

export const ApplicationsTab = () => {
    const classes = useStyles()
    const [activeOnly, setActiveOnly] = useState(false)
    const { list, loading, fetch } = useApplicationList(activeOnly)
    const { create, loading: creating } = useCreateApplication(() => fetch())
    const { update, loading: updating } = useUpdateApplication(() => fetch())
    const { remove, loading: deleting } = useDeleteApplication(() => fetch())

    const [addOpen, setAddOpen] = useState(false)
    const [editApp, setEditApp] = useState<Application | null>(null)
    const [deleteApp, setDeleteApp] = useState<Application | null>(null)

    useEffect(() => {
        fetch()
    }, [fetch])

    const handleAddSubmit = async (payload: ApplicationCreateUpdatePayload) => {
        await create(payload)
        setAddOpen(false)
    }

    const handleEditSubmit = async (
        id: number,
        payload: Partial<ApplicationCreateUpdatePayload>,
    ) => {
        await update(id, payload)
        setEditApp(null)
    }

    const handleDeleteConfirm = async () => {
        if (!deleteApp) return
        await remove(deleteApp.id)
        setDeleteApp(null)
    }

    return (
        <div className={classes.root}>
            <div className={classes.toolbar}>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => setAddOpen(true)}
                >
                    Add application
                </Button>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={activeOnly}
                            onChange={(e) => setActiveOnly(e.target.checked)}
                            color="primary"
                        />
                    }
                    label="Active only"
                />
            </div>

            <TableContainer
                component={Paper}
                className={classes.tableContainer}
            >
                {loading ? (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        minHeight={200}
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Slug</TableCell>
                                <TableCell align="right">
                                    Default price
                                </TableCell>
                                <TableCell>Active</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {list.map((app) => (
                                <TableRow key={app.id}>
                                    <TableCell>{app.name}</TableCell>
                                    <TableCell>{app.slug}</TableCell>
                                    <TableCell align="right">
                                        {formatPrice(app.default_price)}
                                    </TableCell>
                                    <TableCell>
                                        {app.is_active ? 'Yes' : 'No'}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            size="small"
                                            onClick={() => setEditApp(app)}
                                            title="Edit"
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => setDeleteApp(app)}
                                            title="Delete"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            <AddApplicationDialog
                open={addOpen}
                onClose={() => setAddOpen(false)}
                onSubmit={handleAddSubmit}
                loading={creating}
            />

            {editApp && (
                <EditApplicationDialog
                    open={!!editApp}
                    application={editApp}
                    onClose={() => setEditApp(null)}
                    onSubmit={(payload) =>
                        handleEditSubmit(editApp.id, payload)
                    }
                    loading={updating}
                />
            )}

            {deleteApp && (
                <DeleteApplicationDialog
                    open={!!deleteApp}
                    application={deleteApp}
                    onClose={() => setDeleteApp(null)}
                    onConfirm={handleDeleteConfirm}
                    loading={deleting}
                />
            )}
        </div>
    )
}

interface AddApplicationDialogProps {
    open: boolean
    onClose: () => void
    onSubmit: (payload: ApplicationCreateUpdatePayload) => Promise<void>
    loading: boolean
}

const AddApplicationDialog: React.FC<AddApplicationDialogProps> = ({
    open,
    onClose,
    onSubmit,
    loading,
}) => {
    const [name, setName] = useState('')
    const [slug, setSlug] = useState('')
    const [description, setDescription] = useState('')
    const [shortDescription, setShortDescription] = useState('')
    const [defaultPrice, setDefaultPrice] = useState('0.00')
    const [isActive, setIsActive] = useState(true)

    const reset = () => {
        setName('')
        setSlug('')
        setDescription('')
        setShortDescription('')
        setDefaultPrice('0.00')
        setIsActive(true)
    }

    const handleClose = () => {
        reset()
        onClose()
    }

    const handleSubmit = async () => {
        if (!name.trim() || !slug.trim()) return
        await onSubmit({
            name: name.trim(),
            slug: slug.trim(),
            description: description.trim() || undefined,
            short_description: shortDescription.trim() || undefined,
            default_price: defaultPrice,
            is_active: isActive,
        })
        reset()
        onClose()
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Add application</DialogTitle>
            <DialogContent>
                <TextField
                    style={formFieldStyle}
                    fullWidth
                    label="Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    variant="outlined"
                />
                <TextField
                    style={formFieldStyle}
                    fullWidth
                    label="Slug"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    required
                    placeholder="e.g. ezturn, ezfolio"
                    variant="outlined"
                />
                <TextField
                    style={formFieldStyle}
                    fullWidth
                    label="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    multiline
                    variant="outlined"
                />
                <TextField
                    style={formFieldStyle}
                    fullWidth
                    label="Short description"
                    value={shortDescription}
                    onChange={(e) => setShortDescription(e.target.value)}
                    variant="outlined"
                />
                <TextField
                    style={formFieldStyle}
                    fullWidth
                    label="Default price"
                    value={defaultPrice}
                    onChange={(e) => setDefaultPrice(e.target.value)}
                    type="text"
                    inputProps={{ inputMode: 'decimal' }}
                    variant="outlined"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={isActive}
                            onChange={(e) => setIsActive(e.target.checked)}
                            color="primary"
                        />
                    }
                    label="Active"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading || !name.trim() || !slug.trim()}
                >
                    {loading ? 'Creating…' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

interface EditApplicationDialogProps {
    open: boolean
    application: Application
    onClose: () => void
    onSubmit: (
        payload: Partial<ApplicationCreateUpdatePayload>,
    ) => Promise<void>
    loading: boolean
}

const EditApplicationDialog: React.FC<EditApplicationDialogProps> = ({
    open,
    application,
    onClose,
    onSubmit,
    loading,
}) => {
    const [name, setName] = useState(application.name)
    const [slug, setSlug] = useState(application.slug)
    const [description, setDescription] = useState(
        application.description || '',
    )
    const [shortDescription, setShortDescription] = useState(
        application.short_description || '',
    )
    const [defaultPrice, setDefaultPrice] = useState(
        application.default_price || '0.00',
    )
    const [isActive, setIsActive] = useState(application.is_active)

    React.useEffect(() => {
        setName(application.name)
        setSlug(application.slug)
        setDescription(application.description || '')
        setShortDescription(application.short_description || '')
        setDefaultPrice(application.default_price || '0.00')
        setIsActive(application.is_active)
    }, [application])

    const handleSubmit = async () => {
        if (!name.trim() || !slug.trim()) return
        await onSubmit({
            name: name.trim(),
            slug: slug.trim(),
            description: description.trim() || undefined,
            short_description: shortDescription.trim() || undefined,
            default_price: defaultPrice,
            is_active: isActive,
        })
        onClose()
    }

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Edit application</DialogTitle>
            <DialogContent>
                <TextField
                    style={formFieldStyle}
                    fullWidth
                    label="Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    variant="outlined"
                />
                <TextField
                    style={formFieldStyle}
                    fullWidth
                    label="Slug"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    required
                    variant="outlined"
                />
                <TextField
                    style={formFieldStyle}
                    fullWidth
                    label="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    multiline
                    variant="outlined"
                />
                <TextField
                    style={formFieldStyle}
                    fullWidth
                    label="Short description"
                    value={shortDescription}
                    onChange={(e) => setShortDescription(e.target.value)}
                    variant="outlined"
                />
                <TextField
                    style={formFieldStyle}
                    fullWidth
                    label="Default price"
                    value={defaultPrice}
                    onChange={(e) => setDefaultPrice(e.target.value)}
                    variant="outlined"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={isActive}
                            onChange={(e) => setIsActive(e.target.checked)}
                            color="primary"
                        />
                    }
                    label="Active"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading || !name.trim() || !slug.trim()}
                >
                    {loading ? 'Saving…' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

interface DeleteApplicationDialogProps {
    open: boolean
    application: Application
    onClose: () => void
    onConfirm: () => Promise<void>
    loading: boolean
}

const DeleteApplicationDialog: React.FC<DeleteApplicationDialogProps> = ({
    open,
    application,
    onClose,
    onConfirm,
    loading,
}) => (
    <Dialog open={open} onClose={onClose}>
        <DialogTitle>Delete application</DialogTitle>
        <DialogContent>
            <Typography>
                Delete &quot;{application.name}&quot;? This will remove the
                application and all company–application links. This action
                cannot be undone.
            </Typography>
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose}>Cancel</Button>
            <Button
                color="secondary"
                variant="contained"
                onClick={onConfirm}
                disabled={loading}
            >
                {loading ? 'Deleting…' : 'Delete'}
            </Button>
        </DialogActions>
    </Dialog>
)
